import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listResearch, getMarketAlerts, pollStatus, getUser, clearToken, clearSharedCookie } from '../api';
import { BILINGUAL_ENABLED } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';

// Two-letter avatar initials from an email, e.g. "bkict.sonhoang@gmail.com"
// -> "BS" (first letter of the first two dot/underscore/dash-separated
// segments of the local part), falling back to the first two characters
// when there's only one segment. Shown instead of the full address so the
// sidebar footer stays compact — the email is still available via the
// chip's title tooltip.
function initials(email) {
  const local = String(email || '').split('@')[0];
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

const TIER_ICON = { basic: '🔓', advanced: '⭐', elite: '💎' };

// Sections that expand/collapse in place within the sidebar (Nansen-style
// left nav) rather than the old hover-dropdown pattern, which doesn't
// translate to a vertical layout.
const RESEARCH_PATHS = ['/research', '/personnel', '/onchain'];
const ONCHAIN_TRACKING_PATHS = ['/staking', '/flow-graph', '/near-flow', '/aster-monitor', '/hyperliquid-monitor', '/dominance'];

// Shared app shell: left sidebar nav + main content, plus the data several
// pages share (research list for header counts, market alerts, and status
// polling).
export default function Layout() {
  const { t, i18n } = useTranslation();
  const [researches, setResearches]   = useState([]);
  const [marketAlerts, setMarketAlerts] = useState([]);
  const pollingRef = useRef({});
  const location = useLocation();

  const loadList = useCallback(async () => {
    try { const { docs } = await listResearch(); setResearches(docs); }
    catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadList();
    const t = setInterval(loadList, 8000);
    return () => clearInterval(t);
  }, [loadList]);

  const loadAlerts = useCallback(async () => {
    try { const { alerts } = await getMarketAlerts(); setMarketAlerts(alerts || []); }
    catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadAlerts();
    const t = setInterval(loadAlerts, 60000);
    return () => clearInterval(t);
  }, [loadAlerts]);

  const startPolling = useCallback((slug) => {
    if (pollingRef.current[slug]) return;
    pollingRef.current[slug] = setInterval(async () => {
      try {
        const s = await pollStatus(slug);
        if (s.status === 'completed' || s.status === 'failed') {
          clearInterval(pollingRef.current[slug]);
          delete pollingRef.current[slug];
          await loadList();
        }
      } catch {
        clearInterval(pollingRef.current[slug]);
        delete pollingRef.current[slug];
      }
    }, 4000);
  }, [loadList]);

  const runningJobs = researches.filter(r => r.status === 'running' || r.status === 'pending');
  const completedCount = researches.filter(r => r.status === 'completed').length;
  const user = getUser();
  // Every feature requires an Advanced/Elite subscription (enforced by
  // <TierGate> per route) — this flag just drives the 🔒 icon in nav links.
  const locked = !user?.hasAccess;

  // Credits balance — kept in sync via the tf-credits event fired by api.js
  // after every charged action.
  const [credits, setCredits] = useState(user?.credits ?? null);
  useEffect(() => {
    const onCredits = (e) => setCredits(e.detail?.credits ?? null);
    window.addEventListener('tf-credits', onCredits);
    return () => window.removeEventListener('tf-credits', onCredits);
  }, []);

  // Shown once when apiFetch detects a 200 response with a non-JSON body —
  // see api.js's 'tf-network-interference' event. Sticks around until
  // dismissed rather than auto-hiding, since the underlying extension
  // interference doesn't resolve itself and would otherwise keep silently
  // re-triggering on every subsequent failed request.
  const [interference, setInterference] = useState(false);
  useEffect(() => {
    const onInterference = () => setInterference(true);
    window.addEventListener('tf-network-interference', onInterference);
    return () => window.removeEventListener('tf-network-interference', onInterference);
  }, []);

  const handleLogout = () => {
    clearToken();
    clearSharedCookie(); // drop the shared trustflow.vn session so we land on login
    window.location.reload();
  };

  // Full-bleed pages manage their own padding; research keeps the default.
  const fullBleed = location.pathname !== '/' && !location.pathname.startsWith('/research');

  // Mobile: the sidebar becomes an off-canvas drawer toggled by a slim
  // top bar's hamburger button, closing on navigation or outside click —
  // same behavior the old mobile-nav-panel had, just a drawer instead of
  // a dropdown panel.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const hamburgerRef = useRef(null);
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onDoc = (e) => {
      if (sidebarRef.current?.contains(e.target)) return;
      if (hamburgerRef.current?.contains(e.target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [sidebarOpen]);

  const researchActive = RESEARCH_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  const onchainActive = ONCHAIN_TRACKING_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  return (
    <div className="app app-shell">
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`} ref={sidebarRef}>
        <div className="sidebar-logo">
          <img src="/logo-white.png" alt="TrustFlow" className="sidebar-logo-img" />
          <div className="sidebar-logo-text">
            <span className="sidebar-logo-title">TrustFlow</span>
            <span className="sidebar-logo-sub">Research</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <SidebarLink to="/technical" icon="📈" label={t('nav.technical')} locked={locked} />
          <SidebarLink to="/news" icon="📰" label={t('nav.news')} locked={locked} />
          <SidebarLink to="/etf-flow" icon="💵" label={t('nav.etfFlow')} locked={locked} />
          <SidebarLink to="/fundraising" icon="💰" label={t('nav.fundraising')} locked={locked} />
          {/* Free for all users — never shows the lock icon. */}
          <SidebarLink to="/contract-audit" icon="🛡️" label={t('nav.contractAudit')} />

          <SidebarGroup
            icon="📊" label={t('nav.research')} mainTo="/research" active={researchActive} locked={locked}
          >
            <SidebarLink to="/personnel" icon="👤" label={t('nav.personnel')} sub />
            <SidebarLink to="/onchain" icon="⛓️" label={t('nav.onchain')} sub />
          </SidebarGroup>

          <SidebarGroup
            icon="⛓️" label={t('nav.onchainMenu')} mainTo="/staking" active={onchainActive} locked={locked}
          >
            <SidebarLink to="/staking" icon="🥩" label={t('nav.staking')} sub />
            <SidebarLink to="/flow-graph" icon="🕸️" label={t('nav.flowGraph')} sub />
            <SidebarLink to="/near-flow" icon="🔗" label={t('nav.nearFlow')} sub />
            <SidebarLink to="/aster-monitor" icon="🚀" label={t('nav.asterMonitor')} sub />
            <SidebarLink to="/hyperliquid-monitor" icon="⚡" label={t('nav.hyperliquidMonitor')} sub />
            <SidebarLink to="/dominance" icon="🌐" label={t('nav.dominance')} sub />
          </SidebarGroup>

          <SidebarLink to="/knowledge" icon="🧠" label={t('nav.knowledge')} locked={locked} />
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-stats">
            <span className="stat">{completedCount} {t('header.reportsCount')}</span>
            {runningJobs.length > 0 && (
              <span className="stat stat-active">
                <span className="dot-pulse" />
                {runningJobs.length} {t('header.running')}
              </span>
            )}
            {credits != null && <span className="stat" title="Credits">⚡ {credits}</span>}
          </div>
          <div className="sidebar-footer-row">
            {BILINGUAL_ENABLED && <LanguageSwitcher current={i18n.language} />}
            <NavLink to="/guide" className="guide-link-btn" title={t('nav.guide')} aria-label={t('nav.guide')}>?</NavLink>
          </div>
          {user && <UserMenu user={user} onLogout={handleLogout} t={t} />}
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      <div className="app-content">
        <div className="topbar-mobile">
          <button
            ref={hamburgerRef}
            type="button"
            className="hamburger-btn"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-haspopup="true"
            aria-expanded={sidebarOpen}
            aria-label={t('nav.menu')}
          >
            <span className={`hamburger-icon ${sidebarOpen ? 'hamburger-icon-open' : ''}`} />
          </button>
          <img src="/logo-white.png" alt="TrustFlow" className="topbar-mobile-logo" />
        </div>

        {interference && (
          <div className="interference-banner">
            <span>{t('common.networkInterferenceMsg')}</span>
            <button className="interference-dismiss-btn" onClick={() => setInterference(false)}>
              {t('common.dismiss')}
            </button>
          </div>
        )}

        <main className="app-main" style={{ padding: fullBleed ? 0 : undefined }}>
          <Outlet context={{ researches, loadList, marketAlerts, startPolling }} />
        </main>
      </div>
    </div>
  );
}

// A single top-level sidebar item — either a plain link, or (when it has
// children) the clickable label half of a SidebarGroup.
function SidebarLink({ to, icon, label, locked, sub, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `sidebar-link ${sub ? 'sidebar-link-sub' : ''} ${isActive ? 'sidebar-link-active' : ''}`}
      title={locked ? undefined : label}
    >
      <span className="sidebar-link-icon">{locked ? '🔒' : icon}</span>
      <span className="sidebar-link-label">{label}</span>
    </NavLink>
  );
}

// Expand-in-place group (Nghiên cứu / On-chain Tracking) — the label links
// to the section's main page; a separate caret toggles the child list open.
// Auto-expanded whenever the current route is inside the section, so
// navigating there directly (e.g. a bookmark) doesn't hide its own sub-nav.
function SidebarGroup({ icon, label, mainTo, active, locked, children }) {
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);

  return (
    <div className={`sidebar-group ${active ? 'sidebar-group-active' : ''}`}>
      <div className="sidebar-group-head">
        <NavLink to={mainTo} end className="sidebar-link sidebar-group-label" title={locked ? undefined : label}>
          <span className="sidebar-link-icon">{locked ? '🔒' : icon}</span>
          <span className="sidebar-link-label">{label}</span>
        </NavLink>
        <button
          type="button"
          className="sidebar-caret-btn"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={label}
        >
          <span className={`sidebar-caret ${open ? 'sidebar-caret-open' : ''}`}>▾</span>
        </button>
      </div>
      {open && <div className="sidebar-group-children">{children}</div>}
    </div>
  );
}

// User account chip — click to open a small popup with account-scoped
// destinations (Portfolio) and Logout, instead of cluttering the main nav
// with a feature that's personal-data/bookkeeping rather than market data.
function UserMenu({ user, onLogout, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="user-chip user-chip-btn"
        title={user.email || ''}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {user.email && <span className="user-avatar">{initials(user.email)}</span>}
        <span className="user-chip-email">{user.email}</span>
        {user.tier && (
          <span className="tier-icon" title={user.tier.charAt(0).toUpperCase() + user.tier.slice(1)}>
            {TIER_ICON[user.tier] || '👤'}
          </span>
        )}
        <span className="nav-caret-btn">▾</span>
      </button>
      {open && (
        <div className="nav-dropdown-menu user-menu-dropdown">
          <NavLink to="/portfolio" className="nav-dropdown-item" onClick={() => setOpen(false)}>💼 {t('nav.portfolio')}</NavLink>
          <button type="button" className="nav-dropdown-item user-menu-logout" onClick={onLogout}>↩ {t('header.logout')}</button>
        </div>
      )}
    </div>
  );
}
